import { ActionIcon, Button, Collapse, Flex, Group, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronDown, IconChevronUp, IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProofCard } from '@feature/proof/ProofCard';
import { ProofFileUploadModal } from '@feature/proof/ProofFileUploadModal';
import { Proof } from '@feature/proof/ProofTypes';
import { ProofUploadRequest } from '@feature/proof/ProofTypes';
import { RoleType, useCurrentUser } from '@feature/security';
import { Author, Id } from '@feature/utils';

interface ProofActionProps {
  proofs: Proof[];
  entity: string;
  onUpload: (request: ProofUploadRequest) => void;
  onDelete: (proofId: Id) => void;
  isPending?: boolean;
  authors?: Author[];
}

export function ProofAction({ proofs, entity, onUpload, onDelete, isPending = false, authors }: ProofActionProps) {
  const { t } = useTranslation();
  const { currentRole, id: currentUserId } = useCurrentUser();
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [dropdownOpened, { toggle: toggleDropdown }] = useDisclosure(true);
  const [editingProof, setEditingProof] = useState<Proof | null>(null);

  const canUploadProof =
    currentRole === 'DISCIPLINE_ADMIN' ||
    currentRole === 'DEPARTMENT_ADMIN' ||
    currentRole === 'FACULTY_ADMIN' ||
    currentRole === 'UNIVERSITY_ADMIN' ||
    currentRole === 'AUTHOR';

  const handleCreate = () => {
    setEditingProof(null);
    openModal();
  };

  const handleEdit = (proof: Proof) => {
    setEditingProof(proof);
    openModal();
  };

  const handleSubmit = (request: ProofUploadRequest) => {
    onUpload(request);
    closeModal();
  };

  const handleDelete = (proofId: Id) => {
    onDelete(proofId);
  };

  return (
    <>
      <Stack gap="md">
        <Flex
          justify="space-between"
          align="center"
        >
          <Group gap="4px">
            <Text fw="500">{t('proof.title')}</Text>
            <ActionIcon
              onClick={toggleDropdown}
              variant="subtle"
              aria-label={dropdownOpened ? t('proof.collapse') : t('proof.expand')}
            >
              {!dropdownOpened && (
                <IconChevronDown
                  style={{ width: '70%', height: '70%' }}
                  stroke={1.5}
                />
              )}
              {dropdownOpened && (
                <IconChevronUp
                  style={{ width: '70%', height: '70%' }}
                  stroke={1.5}
                />
              )}
            </ActionIcon>
          </Group>
          {canUploadProof && (
            <Button
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={handleCreate}
              loading={isPending}
            >
              {t('proof.create')}
            </Button>
          )}
        </Flex>

        <Collapse in={dropdownOpened}>
          {proofs.length === 0 ? (
            <Text
              size="sm"
              c="dimmed"
              fs="italic"
            >
              {t('proof.noProofs')}
            </Text>
          ) : (
            <Stack gap="xs">
              {proofs.map(proof => (
                <ProofCard
                  key={proof.id}
                  proof={proof}
                  entity={entity}
                  canEdit={canUploadProof}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isPending={isPending}
                />
              ))}
            </Stack>
          )}
        </Collapse>
      </Stack>

      {modalOpened && (
        <ProofFileUploadModal
          onClose={closeModal}
          onSubmit={handleSubmit}
          isSubmitting={isPending}
          isEditing={editingProof !== null}
          existingUserId={editingProof?.owner?.id}
          currentUserRole={currentRole as RoleType}
          currentUserId={currentUserId}
          authors={authors}
        />
      )}
    </>
  );
}
